'use client';

import { useEffect, useRef } from 'react';
import { useStore } from '@/providers/StoreProvider';
import { useStoreContext } from '@repo/store/web';
import type { WebExtraSlices } from '@/providers/StoreProvider';
import {
  refreshSession,
  setApiAuthToken,
  setLogoutHandler,
  setSessionRefreshedHandler,
} from '@/lib/api';
import { logoutWeb } from '@/lib/api-web';

let firebaseAuthPromise: Promise<typeof import('@/lib/auth/firebase')> | null = null;

function getFirebaseAuthModule() {
  if (!firebaseAuthPromise) {
    firebaseAuthPromise = import('@/lib/auth/firebase');
  }
  return firebaseAuthPromise;
}

export function AuthEffects() {
  const store = useStoreContext<WebExtraSlices>();
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const token = useStore(s => s.webAuth.token);
  const customToken = useStore(s => s.webAuth.customToken);
  const profile = useStore(s => s.profile?.profile);
  const setCurrentUserScheduledUnits = useStore(s => s.chat?.setCurrentUserScheduledUnits);
  const fetchSavedUnits = useStore(s => s.savedUnits.fetchSavedUnits);
  const didFetchScheduledUnitsRef = useRef(false);
  const didFetchSavedUnitsRef = useRef(false);
  const sessionExpiresAt = useStore(s => s.webAuth.sessionExpiresAt);
  const sessionExpiresInSeconds = useStore(s => s.webAuth.sessionExpiresInSeconds);
  const setSessionExpiresStamp = useStore(s => s.webAuth.setSessionExpiresStamp);
  const logout = useStore(s => s.webAuth.logout);
  const logoutSilently = useStore(s => s.webAuth.logoutSilently);
  const setProfile = useStore(s => s.profile?.setProfile);
  const clearProfile = useStore(s => s.profile?.clearProfile);
  const clearMessages = useStore(s => s.chat?.clearChatData);
  const clearExplorerData = useStore(s => s.explorer.clearExplorerData);
  const clearSavedUnits = useStore(s => s.savedUnits.clearSavedUnits);
  const resetAuthUI = useStore(s => s.authUI.reset);
  const closeAuthUI = useStore(s => s.authUI.close);
  const setProfileCompletionPromptMutedUntil = useStore(
    s => s.authUI.setProfileCompletionPromptMutedUntil,
  );

  const lastAuthStateRef = useRef<boolean | null>(null);

  // Keep API auth token in sync with store.
  // This enables request interceptors in non-React modules to attach the latest bearer.
  useEffect(() => {
    setApiAuthToken(token ?? customToken ?? null);
  }, [token, customToken]);

  // Cross-tab auth sync via localStorage.
  useEffect(() => {
    const persist = (store as unknown as { persist?: { rehydrate?: () => void } })?.persist;
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'authUIModalMutedUntil') {
        const mutedUntil = JSON.parse(e.newValue ?? 'null');
        if (typeof mutedUntil === 'number') {
          setProfileCompletionPromptMutedUntil(mutedUntil);
          closeAuthUI();
        }
        window.localStorage?.removeItem('authUIModalMutedUntil');
        return;
      }
      if (e.key !== 'firebaseAuthChanged') return;
      let payload: { state?: 'signedin' | 'signedout' } | undefined;
      try {
        payload = e.newValue ? JSON.parse(e.newValue) : undefined;
      } catch {}
      if (payload?.state === 'signedin') {
        if (isLoggedIn) return;
        try {
          persist?.rehydrate?.();
        } catch {
          // best-effort
        }
        return;
      }
      if (payload?.state === 'signedout') {
        if (!isLoggedIn) return;
        closeAuthUI();
        logoutSilently();
        void (async () => {
          try {
            const { getFirebaseAuth } = await getFirebaseAuthModule();
            const auth = getFirebaseAuth();
            await auth?.signOut?.();
          } catch {
            // best-effort
          }
        })();
      }
    };

    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [
    clearExplorerData,
    clearProfile,
    clearSavedUnits,
    closeAuthUI,
    isLoggedIn,
    logoutSilently,
    store,
  ]);

  useEffect(() => {
    if (lastAuthStateRef.current === null) {
      lastAuthStateRef.current = isLoggedIn;
      return;
    }
    if (lastAuthStateRef.current === isLoggedIn) return;
    lastAuthStateRef.current = isLoggedIn;
    try {
      window.localStorage?.setItem(
        'firebaseAuthChanged',
        JSON.stringify({ state: isLoggedIn ? 'signedin' : 'signedout' }),
      );
    } catch {
      // best-effort
    }
  }, [isLoggedIn]);

  useEffect(() => {
    setLogoutHandler(async () => {
      // Also clear the HttpOnly session cookie on the server side
      try {
        await logoutWeb();
      } catch {
        // Best-effort; continue local cleanup even if server logout fails
      }
      // Clear persisted auth UI state (incl. profileCompletionPromptMutedUntil) so it doesn't leak across sessions.
      resetAuthUI();
      clearExplorerData();
      clearSavedUnits();
      clearMessages();
      clearProfile?.();
      logout();
      try {
        const { getFirebaseAuth } = await getFirebaseAuthModule();
        const auth = getFirebaseAuth();
        await auth.signOut();
      } catch {
        // best-effort
      }

      // Note: `logoutWeb()` already broadcasts a cross-tab logout ping.
    });
  }, [clearExplorerData, clearProfile, clearSavedUnits, clearMessages, logout, resetAuthUI]);

  useEffect(() => {
    setSessionRefreshedHandler(({ expiresIn, profile }) => {
      if (typeof expiresIn === 'number') setSessionExpiresStamp(expiresIn);
      if (profile) setProfile?.(profile);
    });
  }, [setSessionExpiresStamp, setProfile]);

  useEffect(() => {
    if (!isLoggedIn || !sessionExpiresAt) return;

    // Refresh the server session cookie when we're within 5 days of expiry.
    // We use the backend-provided `expiresIn` when available to avoid assuming a fixed TTL.
    const COOKIE_TTL_FALLBACK_SEC = 14 * 24 * 60 * 60;
    const cookieTtlSec = sessionExpiresInSeconds ?? COOKIE_TTL_FALLBACK_SEC;
    const REFRESH_WINDOW_SEC = 5 * 24 * 60 * 60;
    let cancelRefresh: (() => void) | null = null;

    const scheduleAt = (targetTimeSec: number, fn: () => void) => {
      const nowSec = Math.floor(Date.now() / 1000);
      const delayMs = Math.max(0, (targetTimeSec - nowSec) * 1000);
      const id = window.setTimeout(fn, delayMs);
      return () => window.clearTimeout(id);
    };

    // If we are already within the refresh window of expiry, refresh immediately.
    const remainingSec = sessionExpiresAt - Math.floor(Date.now() / 1000);
    if (remainingSec <= REFRESH_WINDOW_SEC) {
      void refreshSession({ holdRequests: remainingSec <= 0 });
    }

    // Schedule refresh for when we enter the refresh window.
    cancelRefresh = scheduleAt(
      sessionExpiresAt - REFRESH_WINDOW_SEC,
      () => void refreshSession({ holdRequests: false }),
    );

    return () => {
      cancelRefresh?.();
    };
  }, [isLoggedIn, sessionExpiresAt, sessionExpiresInSeconds]);

  useEffect(() => {
    if (!isLoggedIn || !profile || !setCurrentUserScheduledUnits) {
      didFetchScheduledUnitsRef.current = false;
      return;
    }
    if (didFetchScheduledUnitsRef.current) return;
    didFetchScheduledUnitsRef.current = true;
    setCurrentUserScheduledUnits().catch((error: unknown) => {
      console.error('Error fetching scheduled units:', error);
    });
  }, [isLoggedIn, profile, setCurrentUserScheduledUnits]);

  // Fetch saved units once after login + profile are ready (source of truth for Save buttons).
  useEffect(() => {
    if (!isLoggedIn || !profile || !fetchSavedUnits) {
      didFetchSavedUnitsRef.current = false;
      return;
    }
    if (didFetchSavedUnitsRef.current) return;
    didFetchSavedUnitsRef.current = true;
    fetchSavedUnits().catch((error: unknown) => {
      console.error('Error fetching saved units:', error);
    });
  }, [isLoggedIn, profile]);

  return null;
}
